import { useState, useEffect } from "react";

// Third Party
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Formik, Field, FieldRenderProps } from "formik";

// UI
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";

// Third Party
import type { MutateLandAsset } from "@/types/soe";

// Services
import { createLandAsset } from "@/services/soe";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/land/add/",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const theid = localStorage.getItem("companyId") || "";
	const id = theid.replace(/^"(.+)"$/, "$1");

	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	// Mutations
	const createLandAssetMutation = useMutation({
		mutationFn: (data: MutateLandAsset) => createLandAsset(data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["lands", id],
			});
			toast({
				title: "ສຳເລັດ",
				description: "ສ້າງຂໍ້ມູນທີ່ດິນສຳເລັດ",
			});
			navigate({
				to: "..",
				replace: true,
			});
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	// Functions
	const handleOnSubmit = (values: MutateLandAsset) => {
		createLandAssetMutation.mutate(values);
	};

	return (
		<div className="space-y-[24px]">
			<div className="text-[18px]">
				<h2 className="text-zinc-950">ຂ້ໍມູນທີ່ດິນ</h2>
				<p className="text-zinc-500">
					ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນຄະນະອຳນວຍການຂອງລັດວິສາຫະກິດ
				</p>

				<Separator className="my-6" />
			</div>

			<Formik
				initialValues={{
					companyId: id,
					yearly: 0,
					landArea: "",
					landType: "",
					totalArea: "",
					location: "",
					govFundedValue: "",
					ownerFundedValue: "",
					currentValue: "",
					usage: "",
					govFunded: "",
				}}
				onSubmit={(values: MutateLandAsset) => {
					handleOnSubmit(values);
				}}
			>
				{({ handleSubmit }) => (
					<form onSubmit={handleSubmit} className="space-y-[16px]">
						<div>
							<Label htmlFor="yearly">
								ປີ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="yearly"
								placeholder="ປີ..."
								type="number"
							/>
						</div>
						<div>
							<Label htmlFor="landArea">
								ເນື້ອໃນທີ່ດິນ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="landArea"
								placeholder="ເນື້ອທີ່ດິນ..."
							/>
						</div>
						<div>
							<Label htmlFor="landType">
								ປະເພດທີ່ດິນ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="landType"
								placeholder="ປະເພດທີ່ດິນ..."
							/>
						</div>
						<div>
							<Label htmlFor="totalArea">
								ເນື້ອທີ່ທັງໝົດ (ມ2/ຮຕ) <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="totalArea"
								placeholder="ເນື້ອທີ່ທັງໝົດ..."
							/>
						</div>
						<div>
							<Label htmlFor="location">
								ທີ່ຕັ້ງຂອງດິນ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="location"
								placeholder="ທີ່ຕັ້ງຂອງດິນ..."
							/>
						</div>
						<div>
							<Label htmlFor="govFundedValue">
								ທືນໃນການຈັດຊື້
								<span className="text-red-500">*</span>
							</Label>
							{/* Selector */}

							<Field name="govFunded">
								{({
									field,
									form,
								}: { field: FieldProps; form: FormikProps }) => (
									<Select
										onValueChange={(value) =>
											form.setFieldValue(field.name, value)
										}
										defaultValue={field.value}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder="ເລືອກປະເພດທຶນ..." />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="ລັດປະກອບທຶນ">ລັດປະກອບທຶນ</SelectItem>
											<SelectItem value="ວິສາຫະກິດຊື້ເອງ">ວິສາຫະກິດຊື້ເອງ</SelectItem>
										</SelectContent>
									</Select>
								)}
							</Field>
						</div>
						<div>
							<Label htmlFor="govFundedValue">
								ມູນຄ່າເບື້ອງຕົ້ນ
								<span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								type="text"
								required
								name="govFundedValue"
								placeholder="ມູນຄ່າເລິ່ມຕົ້ນ..."
							/>
						</div>
						<div>
							<Label htmlFor="currentValue">
								ມູນຄ່າໃນປັດຈຸບັນ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								type="text"
								required
								name="currentValue"
								placeholder="ມູນຄ່າໃນປັດຈຸບັນ..."
							/>
						</div>
						<div>
							<Label htmlFor="usage">
								ການນຳໃຊ້ທີ່ດິນ <span className="text-red-500">*</span>
							</Label>
							<Field
								as={Input}
								required
								name="usage"
								placeholder="ການນຳໃຊ້ທີ່ດິນ..."
							/>
						</div>
						<div>
							<Label htmlFor="usage">
								ໝາຍເຫດ <span className="text-red-500">*</span>
							</Label>
							<Field as={Input} required name="usage" placeholder="ໝາຍເຫດ..." />
						</div>

						<div className="flex gap-4 justify-center items-center w-full">
							<Button
								disabled={createLandAssetMutation.isPending}
								type="button"
								variant={"outline"}
								onClick={() => navigate({ to: "..", replace: true })}
							>
								ຍົກເລີກ
							</Button>
							<Button type="submit">
								{createLandAssetMutation.isPending ? "ກຳລັງດຳເນີນການ..." : "ບັນທຶກ"}
							</Button>
						</div>
					</form>
				)}
			</Formik>
		</div>
	);
}
